// src/components/AutocompleteTagFilter.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosBackend from "../api/axiosBackend";

// shadcn
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "components/ui/command";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";

// icons
import { ChevronDown, X } from "lucide-react";

const normalizeTagsResponse = (res) => {
  const data = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(data) ? data : [];
};

const AutocompleteTagFilter = ({ selectedTags, setSelectedTags }) => {
  const [tags, setTags] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axiosBackend.get("/tags/get");
        setTags(normalizeTagsResponse(response));
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchTags();
  }, []);

  const filteredTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selectedIds = new Set((selectedTags || []).map((t) => t._id));
    const base = !q ? tags : tags.filter((t) => (t.name || "").toLowerCase().includes(q));
    return base.filter((t) => !selectedIds.has(t._id));
  }, [query, tags, selectedTags]);

  const handleTagSelect = (tag) => {
    if (!selectedTags.some((t) => t._id === tag._id)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setQuery("");
    setOpen(false);
  };

  const handleTagRemove = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t._id !== tag._id));
  };

  return (
    <div className="w-full space-y-2" dir="rtl">
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        {/* Input-like trigger (single click/tap) */}
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 text-sm shadow-sm transition-colors hover:bg-accent/20 focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="סינון לפי תגיות"
          >
            <span className="text-muted-foreground">סינון לפי תג…</span>
            <ChevronDown className="h-4 w-4 opacity-70" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 max-h-[70vh] overflow-hidden"
          align="end"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => inputRef.current?.focus());
          }}
          onInteractOutside={(e) => {
            // CRITICAL: allow touch scrolling inside popover
            e.preventDefault();
          }}
        >

          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                ref={inputRef}
                placeholder="חפש תג…"
                value={query}
                onValueChange={setQuery}
              />

              {query.trim() && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 h-7 w-7 -translate-y-1/2"
                  onClick={() => {
                    setQuery("");
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                  aria-label="נקה חיפוש"
                  title="נקה"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <CommandList
              className="max-h-64 overflow-y-auto overscroll-contain touch-pan-y"
              style={{
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",   // IMPORTANT for mobile
              }}
              onWheelCapture={(e) => e.stopPropagation()}
              onTouchMoveCapture={(e) => e.stopPropagation()} // mobile equivalent of wheel
            >
              <CommandEmpty>לא נמצאו תגיות</CommandEmpty>
              <CommandGroup>
                {filteredTags.map((tag) => (
                  <CommandItem
                    key={tag._id}
                    value={tag.name}
                    onSelect={() => handleTagSelect(tag)}
                    className="cursor-pointer"
                  >
                    {tag.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>

          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected tags */}
      <div className="flex flex-wrap gap-2">
        {selectedTags.map((tag) => (
          <Badge
            key={tag._id}
            variant="secondary"
            className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
          >
            <span>{tag.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-1 h-5 w-5 p-0"
              onClick={() => handleTagRemove(tag)}
              aria-label={`הסר תג ${tag.name}`}
              title="הסר"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default AutocompleteTagFilter;
